import React, { useContext, useEffect, useState } from 'react'
import { makeStyles, Grid, Button, useMediaQuery, TranslationLanguageMenu, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useDialogs, useUser } from '@paintscout/ui-business'
import GetAppIcon from '@material-ui/icons/GetApp'

import {
  getObjectLabels,
  getQuoteOptions,
  hasIntegrationInterface,
  hasProcessingPayment,
  isSignatureRequired,
  PAYMENT_PENDING_MESSAGE
} from '@paintscout/util/builder'
import { useSnackbar } from 'notistack'
import { fetchFreshQuote } from '../../../util/freshQuoteState'
import { getFreshPayAmount, getProcessingTotal } from './getFreshPayAmount'

import CheckIcon from '@material-ui/icons/Check'
import MoneyIcon from '@material-ui/icons/AttachMoney'

import getDepositAmount from '@paintscout/util/util/getDepositAmount/getDepositAmount'
import { openProcessingPaymentWarningDialog } from '../../../util/openProcessingPaymentWarningDialog'
import PresentationNavMore from '../PresentationNavMore'
import PresentationNavTotal from '../PresentationNavTotal'
import { usePresentationNav } from '../PresentationNavContext'

import { QuoteContext } from '../../../context/QuoteContext'

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      textAlign: 'right'
      // height: '100%'
    },
    acceptGridRoot: {
      height: '100%',
      padding: theme.spacing(1, 0),
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(1.5, 0)
      }
    },

    button: {
      marginBottom: theme.spacing(0.5),
      [theme.breakpoints.up('lg')]: {
        marginLeft: theme.spacing(2),
        marginBottom: 0
      }
    }
  }
})

export interface PresentationAcceptButtonProps {
  type?: 'open' | 'closed'
  showMore?: boolean
  showPrice?: boolean
}

export default function PresentationAcceptButton({
  type,
  showMore,
  showPrice = true,
  ...props
}: PresentationAcceptButtonProps) {
  const classes = useStyles(props)
  const alignment = getAlignment(type)

  const { options } = useClientOptions()
  const { preferences } = useUser()
  const { openDialog, dismissDialog } = useDialogs()
  const [responding, setResponding] = useState(false)
  const {
    view,
    allowResponse,
    hasPending,
    onAcceptClick,
    onPayClick,
    onMoreClick,
    revision,
    onLanguageChange,
    availableLangs
  } = usePresentationNav()
  const { lang, __ } = useTranslation()
  const [variant, _] = useState(hasPending ? 'additional' : 'quote')
  const { quote, isCustomerView, refetchQuote } = useContext(QuoteContext)
  const { enqueueSnackbar } = useSnackbar()
  const [checkingPayment, setCheckingPayment] = useState(false)
  const {
    allowPaymentsOnInvoices: allowCCPaymentsOnInvoices,
    allowACHPaymentsOnInvoices,
    allowPaymentsOnQuotes,
    depositAmount
  } = getQuoteOptions({
    quote,
    options
  })

  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })

  useEffect(() => {
    if (variant === 'quote' && responding && quote.status.value === 'accepted') {
      setResponding(false)
    }
    if (variant === 'additional' && responding && !hasPending) {
      setResponding(false)
    }
  }, [responding, hasPending, quote.status.value])

  const objectLabels = getObjectLabels({ options, quote })

  const smDown = useMediaQuery('sm')
  const xlUp = useMediaQuery('xl', { up: true })

  const buttonLabel = getButtonLabel({ objectLabels, smDown })

  const showMoreButton = (type === 'open' || showMore) && onMoreClick
  // Work orders get an inline language dropdown + Download button in place of the "More"
  // popup, matching the translation design (a deal-opened work order and a link-opened one
  // share the same controls).
  const isWorkOrder = view === 'work-order'

  const processingTotal = getProcessingTotal(quote)
  const effectiveBalance = Math.max(0, quote.totals.balance_due - processingTotal)
  const depositAmountDollars = getDepositAmount({ quote, options })
  const effectiveDepositAmount = Math.max(0, depositAmountDollars - processingTotal)

  const hasPayments = (hasStripe || hasStripeW1d || hasPayrix) && !!onPayClick && quote.totals.balance_due > 0

  const hideButtonViews = ['work-order']
  const hideButtonStatus = ['invoiced', 'declined']
  const allowQuotePayment =
    hasPayments &&
    allowPaymentsOnQuotes &&
    !quote.is_invoice &&
    !!depositAmount.value &&
    effectiveDepositAmount > 0 &&
    quote.totals.amount_paid === 0 &&
    quote.status.value === 'accepted' &&
    !quote.depositInfo &&
    !hideButtonViews.includes(view)

  const allowInvoicePayment =
    hasPayments &&
    (allowCCPaymentsOnInvoices || allowACHPaymentsOnInvoices) &&
    quote.is_invoice &&
    effectiveBalance > 0 &&
    !hideButtonViews.includes(view)

  const hasPaymentError = quote.paymentError

  // Branch on freshly-fetched state, not page-load state — a prior attempt may have flagged the
  // quote, or another payment may have gone processing since this page was loaded (mirrors
  // PaymentRequest.handlePay).
  const handlePayClick = async () => {
    if (checkingPayment) return
    setCheckingPayment(true)
    const fresh = (await fetchFreshQuote(refetchQuote)) ?? quote
    setCheckingPayment(false)

    if (fresh.paymentError) {
      // refetchQuote pushed the fresh doc into context, so disabled={hasPaymentError} engages.
      enqueueSnackbar(__(PAYMENT_PENDING_MESSAGE), { variant: 'error' })
      return
    }

    if (allowQuotePayment && fresh.depositInfo && fresh.depositInfo.status !== 'processing') {
      // Deposit completed while this page was stale — nothing left to pay from this button, and
      // "payment pending" would be a lie. The refetch re-render removes the button.
      return
    }

    const freshAmount = getFreshPayAmount({ quote: fresh, options, deposit: allowQuotePayment })
    if (freshAmount <= 0) {
      // Fully covered by in-flight payments — the refetch re-render also hides/disables the button.
      enqueueSnackbar(__(PAYMENT_PENDING_MESSAGE), { variant: 'error' })
      return
    }

    const doPayClick = () => onPayClick(null, freshAmount, hasPayrix)
    if (hasProcessingPayment({ quote: fresh })) {
      openProcessingPaymentWarningDialog({
        options,
        quote: fresh,
        openDialog,
        dismissDialog,
        onConfirm: doPayClick
      })
    } else {
      doPayClick()
    }
  }

  const showAcceptButton =
    (!hideButtonStatus.includes(quote.status.value) &&
      !hideButtonViews.includes(view) &&
      (hasPending || allowResponse) &&
      onAcceptClick) ||
    responding

  const showTotal = showPrice

  const requireSignature = isSignatureRequired({
    options,
    quote,
    path: hasPending
      ? 'requireSignatureAdditionalWork'
      : isCustomerView
        ? 'requireSignatureSent'
        : 'requireSignatureOnSpot'
  })

  return (
    <div className={classes.root}>
      <Grid
        container
        spacing={1}
        wrap="nowrap"
        direction={type === 'open' ? 'column' : 'row'}
        alignItems={type === 'open' ? 'flex-end' : 'center'}
        justifyContent="flex-end"
        classes={{ container: classes.acceptGridRoot }}
      >
        <Grid item>
          <Grid container {...alignment} wrap="nowrap" direction={xlUp ? 'row-reverse' : 'column'}>
            {(allowQuotePayment || allowInvoicePayment) && (
              <Grid item>
                <Button
                  icon={MoneyIcon}
                  classes={{ root: classes.button }}
                  disabled={hasPaymentError}
                  loading={checkingPayment}
                  onClick={handlePayClick}
                >
                  {allowQuotePayment ? 'Pay Deposit' : 'Pay Now'}
                </Button>
              </Grid>
            )}
            {showAcceptButton && (
              <Grid item className={'customer-view-accept-button'}>
                <Button
                  loading={responding}
                  icon={CheckIcon}
                  disabled={!allowResponse || revision}
                  classes={{ root: classes.button }}
                  onClick={async (e) => {
                    if (!requireSignature) {
                      setResponding(true)
                    }
                    onAcceptClick(e, hasPending)
                  }}
                >
                  {buttonLabel}
                </Button>
              </Grid>
            )}
            {showTotal && (
              <Grid item>
                <PresentationNavTotal type={type} />
              </Grid>
            )}
          </Grid>
        </Grid>
        {showMoreButton &&
          (isWorkOrder ? (
            <>
              {(availableLangs?.length ?? 0) > 0 && (
                <Grid item>
                  <TranslationLanguageMenu
                    currentLang={lang}
                    availableLangs={availableLangs}
                    onChange={(l) => onLanguageChange?.(l === 'en' ? undefined : l)}
                  />
                </Grid>
              )}
              <Grid item>
                <Button
                  variant={'text'}
                  icon={GetAppIcon}
                  classes={{ root: classes.button }}
                  onClick={(ev) => onMoreClick(ev, 'download')}
                >
                  {__('Download')}
                </Button>
              </Grid>
            </>
          ) : (
            <Grid item>
              <PresentationNavMore />
            </Grid>
          ))}
      </Grid>
    </div>
  )

  function getButtonLabel({ objectLabels, smDown }) {
    if (objectLabels.acceptButton) {
      return objectLabels.acceptButton.value
    } else if (smDown) {
      return 'Accept'
    } else {
      const item = hasPending ? objectLabels.additionalWork.value : objectLabels.quote.value
      return `Accept ${item}`
    }
  }

  function getAlignment(type) {
    const spacing = 0 as any
    const direction = 'row-reverse' as any
    const alignItems = 'center' as any
    const justify = 'flex-end' as any

    if (type === 'open') {
      // spacing = 0 as any
      // direction = 'column' as any
      // alignItems = 'flex-end' as any
      // justify = 'flex-start' as any
    }

    return {
      spacing,
      direction,
      alignItems,
      justify
    }
  }
}
